/**
 * Secure Token Manager
 *
 * Implements secure token storage using httpOnly cookies instead of localStorage
 * to prevent XSS attacks and improve security.
 */

import { log } from "../config/env";
import { TOKEN_STORAGE_KEYS } from "./constants";

export interface TokenData {
  access_token: string;
  token_type: string;
  expires_in?: number;
}

export class SecureTokenManager {
  private static instance: SecureTokenManager | null = null;
  private token: string | null = null;
  private tokenExpiry: number | null = null;

  constructor() {
    // Initialize token from httpOnly cookie (handled by browser)
    this.initializeToken();
  }

  static getInstance(): SecureTokenManager {
    if (!SecureTokenManager.instance) {
      SecureTokenManager.instance = new SecureTokenManager();
    }
    return SecureTokenManager.instance;
  }

  private initializeToken(): void {
    // Token is automatically included in requests via httpOnly cookies
    // We don't need to store it in localStorage anymore
    this.token = null;
    this.tokenExpiry = null;

    log("info", "🔒 Secure token manager initialized with httpOnly cookies");
  }

  /**
   * Set token data (for backward compatibility during migration)
   * SECURITY: Tokens are stored in httpOnly cookies by backend - no client-side storage needed
   * This method is kept for backward compatibility but does not store tokens
   */
  async setToken(tokenData: TokenData): Promise<void> {
    try {
      // SECURITY: Tokens are stored in httpOnly cookies by backend
      // We don't store tokens in localStorage/sessionStorage (XSS vulnerability)
      // Only track expiry for client-side logic if needed
      if (tokenData.expires_in) {
        this.tokenExpiry = Date.now() + tokenData.expires_in * 1000;
      }

      log(
        "info",
        "🔒 Token set via httpOnly cookies by backend - no client-side storage"
      );
    } catch (error) {
      log("error", "❌ Failed to process token data:", error);
      // Don't throw - tokens are in cookies anyway
    }
  }

  /**
   * Get current token
   * SECURITY: Tokens are in httpOnly cookies - not accessible via JavaScript
   * This method returns null as tokens are handled automatically by browser
   * Only used for backward compatibility with code that checks for token existence
   */
  getToken(): string | null {
    // SECURITY: Tokens are in httpOnly cookies, not accessible via JavaScript
    // Browser automatically sends cookies with requests when withCredentials: true
    // Return null to indicate we rely on cookies (not localStorage/sessionStorage)
    return null;
  }

  /**
   * Check if token is valid and not expired
   */
  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check expiry
    if (this.tokenExpiry && Date.now() > this.tokenExpiry) {
      this.clearToken();
      return false;
    }

    return true;
  }

  /**
   * Clear token data
   * SECURITY: Tokens are cleared by backend on logout via cookie deletion
   * This method only clears legacy localStorage tokens for migration
   */
  clearToken(): void {
    this.token = null;
    this.tokenExpiry = null;

    // Clear any legacy localStorage tokens (for migration)
    // Main tokens are cleared by backend via cookie deletion
    localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(TOKEN_STORAGE_KEYS.AUTH_TOKEN);

    log(
      "info",
      "🔒 Legacy tokens cleared - main tokens cleared by backend via cookies"
    );
  }

  /**
   * Get token expiry time
   */
  getTokenExpiry(): number | null {
    return this.tokenExpiry;
  }

  /**
   * Check if token will expire soon (within 5 minutes)
   */
  willExpireSoon(): boolean {
    if (!this.tokenExpiry) return false;

    const fiveMinutes = 5 * 60 * 1000;
    return this.tokenExpiry - Date.now() < fiveMinutes;
  }

  /**
   * Refresh token using httpOnly cookie refresh token
   */
  async refreshToken(): Promise<boolean> {
    try {
      // Call refresh endpoint - refresh token is in httpOnly cookie
      const { authAPI } = await import("../services/authApi");
      await authAPI.refresh();

      log("info", "🔄 Token refreshed successfully via httpOnly cookie");
      return true;
    } catch (error) {
      log("error", "❌ Token refresh failed:", error);
      return false;
    }
  }

  /**
   * Get security status
   */
  getSecurityStatus(): {
    isSecure: boolean;
    storageMethod: "httpOnly" | "sessionStorage" | "localStorage" | "none";
    hasToken: boolean;
    isExpired: boolean;
    willExpireSoon: boolean;
  } {
    const hasToken = this.isTokenValid();
    const isExpired = !hasToken && this.tokenExpiry !== null;

    return {
      isSecure: process.env.NODE_ENV === "production",
      storageMethod:
        process.env.NODE_ENV === "production" ? "httpOnly" : "sessionStorage",
      hasToken,
      isExpired,
      willExpireSoon: this.willExpireSoon(),
    };
  }
}

// Export singleton instance
export const secureTokenManager = SecureTokenManager.getInstance();

// Export utility functions for backward compatibility
export const getSecureToken = (): string | null =>
  secureTokenManager.getToken();
export const setSecureToken = (tokenData: TokenData): Promise<void> =>
  secureTokenManager.setToken(tokenData);
export const clearSecureToken = (): void => secureTokenManager.clearToken();
export const isSecureTokenValid = (): boolean =>
  secureTokenManager.isTokenValid();
